import { ref, onBeforeUnmount } from 'vue';
import ApiService from '@/core/services/ApiService';
import moment from 'moment';

/**
 * Composable para gerenciar execução de certificação
 * 
 * Encapsula lógica de timer, máquinas e deadline compartilhada entre componentes.
 * O deadline é calculado pelo backend baseado no campo deadline_days de cada step.
 * 
 * Uso:
 * - CertificationToDo: usa data.deadline (step de execution)
 * - CertificationSendingReport: usa data.deadline_send_report (step de report)
 * - CertificationModeling: usa data.certification_data.modeling_deadline (estratégia CASP)
 * 
 * @param certificationId - ID da certificação
 */
export function useCertificationExecution(certificationId: string) {
  const machines = ref([]);
  const timeRemaining = ref({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const deadline = ref<string>();
  const created_at = ref<string>();
  const showMachine = ref(false);
  const intervalId = ref<number>();

  // Cleanup do interval quando componente for desmontado
  onBeforeUnmount(() => {
    if (intervalId.value) {
      clearInterval(intervalId.value);
    }
  });

  /**
   * Calcula tempo restante até o deadline
   * Subtrai 3 horas do cálculo (timezone adjustment)
   */
  const calculateTimeRemaining = () => {
    if (!deadline.value) {
      timeRemaining.value = { days: 0, hours: 0, minutes: 0, seconds: 0 };
      return;
    }

    const now: any = new Date();
    const end: any = moment.utc(deadline.value).toDate();

    const difference = end - now;
    if (difference > 0) {
      timeRemaining.value = {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / (1000 * 60)) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    } else {
      timeRemaining.value = { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }
  };

  /**
   * Busca máquinas associadas à certificação
   */
  const getMachines = async () => {
    try {
      const machinesResponse = await ApiService.get(`/certification/index/machines/${certificationId}`);
      if (machinesResponse.status === 200) {
        machines.value = machinesResponse.data.data;
      }
    } catch (e) {
      console.error("Erro ao buscar as máquinas:", e);
      throw e;
    }
  };

  /**
   * Inicia intervalo de contagem regressiva
   * @param onExpired - Callback executado quando timer expirar
   */
  const startCountdownInterval = (onExpired?: () => void) => {
    const interval = setInterval(() => {
      calculateTimeRemaining();
      if (
        timeRemaining.value.days === 0 &&
        timeRemaining.value.hours === 0 &&
        timeRemaining.value.minutes === 0 &&
        timeRemaining.value.seconds <= 1
      ) {
        clearInterval(interval);
        if (onExpired) {
          onExpired();
        }
      }
    }, 1000);
    intervalId.value = interval as any;
  };

  /**
   * Inicializa máquinas e inicia countdown
   * @param onExpired - Callback executado quando timer expirar
   */
  const initializeMachinesAndCountdown = async (onExpired?: () => void) => {
    await getMachines();
    showMachine.value = true;
    calculateTimeRemaining();
    startCountdownInterval(onExpired);
  };

  /**
   * Busca deadline e created_at do status da certificação
   * Retorna todo o objeto de dados do status para acesso flexível aos deadlines
   */
  const fetchDeadline = async () => {
    try {
      const { data, status } = await ApiService.get(`/certification/status/${certificationId}`);
      
      if (status === 200) {
        // Popula deadline principal (usado por steps de execution)
        deadline.value = data.data.deadline;
        created_at.value = data.data.created_at;
        
        // Retorna o objeto completo para permitir acesso a outros campos
        // como deadline_send_report ou certification_data.modeling_deadline
        return data.data;
      }
    } catch (e) {
      console.error("Erro ao buscar o deadline:", e);
      throw e;
    }
  };

  /**
   * Para o intervalo de countdown
   */
  const stopCountdown = () => {
    if (intervalId.value) {
      clearInterval(intervalId.value);
      intervalId.value = undefined;
    }
  };

  return {
    // State
    machines,
    timeRemaining,
    deadline,
    created_at,
    showMachine,
    
    // Methods
    calculateTimeRemaining,
    getMachines,
    startCountdownInterval,
    initializeMachinesAndCountdown,
    fetchDeadline,
    stopCountdown,
  };
}
